<template>
  <div class="v-suggestions" :class="withoutSearchIcon ? 'without-search-icon' : ''">
    <input type="search" :disabled="disabled" :class="extendedOptions.inputClass" @keydown="onKeyDown" @blur="hideItems" @focus="showItems = true" v-model="query" :autocomplete="Math.random()" :placeholder="extendedOptions.placeholder" :aria-label="extendedOptions.placeholder" @keydown.tab="onKeyDown" :required="required">
    <div class="suggestions">
      <ul class="items" v-show="items && items.length > 0 && showItems === true" v-bind:class="{'text-muted': searching}">
        <li v-if="searching" class="text-center">
          <b-spinner variant="primary" small />
        </li>
        <li class="item" :key="index" v-for="(item, index) in items" @click.prevent="selectItem(index)" v-bind:class="{ 'is-active': index === activeItemIndex }">
          <!--
            @slot Result Item
            @binding {object} item object
          -->
          <slot name="item" :item="item">
            {{item}}
          </slot>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import debounce from 'debounce'

/**
   * The Autocomplete base.
   * @displayName Autocomplete
   */
export default {
  emits: ['input'],
  name: 'Autocomplete',
  inheritAttributes: true,
  props: {
    /**
     * Array of static response kind objects to be shown before remote response.
     */
    staticItems: {
      type: Array,
      default: () => [],
      required: false
    },
    options: {
      type: Object,
      default: () => {}
    },
    onInputChange: {
      type: Function,
      required: true
    },
    onItemSelected: {
      type: Function
    },
    value: {
      type: String,
      required: true
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    withoutSearchIcon: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const defaultOptions = {
      debounce: 0,
      placeholder: '',
      inputClass: 'input'
    }
    const extendedOptions = { ...defaultOptions, ...this.options }
    return {
      extendedOptions,
      query: this.value,
      lastSetQuery: null,
      items: this.staticItems,
      activeItemIndex: -1,
      showItems: false,
      searching: false
    }
  },
  beforeMount() {
    if (this.extendedOptions.debounce !== 0) {
      this.onQueryChanged = debounce(this.onQueryChanged, this.extendedOptions.debounce)
    }
  },
  watch: {
    query: function (newValue, oldValue) {
      if (newValue === this.lastSetQuery) {
        this.lastSetQuery = null
        return
      }
      this.onQueryChanged(newValue)
      /**
       * Input event.
       * @property {String} newValue new value set
       */
      this.$emit('input', newValue)
    },
    value: function (newValue, oldValue) {
      this.setInputQuery(newValue)
    },
    staticItems: {
      deep: true,
      handler(newValue) {
        this.items = newValue
      }
    }
  },
  methods: {
    onItemSelectedDefault(item) {
      if (typeof item === 'string') {
        this.$emit('input', item)
        this.setInputQuery(item)
        this.showItems = false
      }
    },
    hideItems() {
      setTimeout(() => {
        this.showItems = false
      }, 300)
    },
    showResults() {
      this.showItems = true
    },
    setInputQuery(value) {
      this.lastSetQuery = value
      this.query = value
    },
    onKeyDown(e) {
      switch (e.keyCode) {
        case 40:
          this.highlightItem('down')
          e.preventDefault()
          break
        case 38:
          this.highlightItem('up')
          e.preventDefault()
          break
        case 13:
          this.selectItem()
          e.preventDefault()
          break
        case 9:
          this.selectItem()
          break
        case 27:
          this.showItems = false
          e.preventDefault()
          break
        default: {
          const charRegexp = new RegExp(/[a-z0-9+-]/i)
          if (e.key.length === 1 && charRegexp.test(e.key)) {
            this.searching = true
          }
          return true
        }
      }
    },
    selectItem(index) {
      let item = null
      if (index === undefined) {
        if (this.activeItemIndex === -1) {
          return
        }
        item = this.items[this.activeItemIndex]
      } else {
        item = this.items[index]
      }
      if (!item) {
        return
      }
      if (this.onItemSelected) {
        this.onItemSelected(item)
      } else {
        this.onItemSelectedDefault(item)
      }
      this.hideItems()
    },
    highlightItem(direction) {
      if (this.items.length === 0) {
        return
      }
      let selectedIndex = this.items.findIndex((item, index) => {
        return index === this.activeItemIndex
      })
      if (selectedIndex === -1) {
        // nothing selected
        if (direction === 'down') {
          selectedIndex = 0
        } else {
          selectedIndex = this.items.length - 1
        }
      } else {
        this.activeIndexItem = 0
        if (direction === 'down') {
          selectedIndex++
          if (selectedIndex === this.items.length) {
            selectedIndex = 0
          }
        } else {
          selectedIndex--
          if (selectedIndex < 0) {
            selectedIndex = this.items.length - 1
          }
        }
      }
      this.activeItemIndex = selectedIndex
    },
    setItems(items) {
      this.items = items
      this.activeItemIndex = -1
      this.showItems = true
      this.searching = false
    },
    preSetItems(items) {
      this.items = items
      this.activeItemIndex = -1
    },
    onQueryChanged(value) {
      const result = this.onInputChange(value)
      if (result === undefined || typeof result === 'boolean' || result === null) {
        this.searching = false
        return
      }
      if (Array.isArray(result)) {
        this.setItems(result)
      } else if (typeof result.then === 'function') {
        result.then(items => {
          this.setItems(items)
        })
      }
    }
  }
}
</script>

<style lang="scss">
.v-suggestions {
  width: 100%;
  position: relative;
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;

  &:before {
    content: '';
    display: inline-block;
    width: 14px;
    height: 14px;
    background: transparent url(@/assets/img/ic-autocomplete-search.svg) no-repeat 50% 50%;
    background-size: contain;
    position: absolute;
    top: 50%;
    left: 10px;
    transform: translateY(-50%)
  }

  input {
    width: inherit !important;
    padding-left: 30px !important;
  }

  &.without-search-icon {
    &:before {
      width: 0;
      height: 0;
    }
    input {
      padding-left: 10px !important;
    }
  }

  .suggestions {
    position: absolute;
    left: 0;
    /* top: 36px; */
    width: 100%;
    z-index: 100;
    background: #fff;
    color: #212529;
  }

  .items {
    list-style: none;
    border: 1px solid #ccc;
    margin: 0;
    padding: 0;
    border-width: 0 1px 1px 1px;
    max-height: 300px;
    overflow-y: auto;
    font-size: .95em;
    .item {
      border-bottom: 1px solid #eee;
      padding: .4rem;
      &.is-active,
      &:hover {
        background: rgba(0, 180, 255, 0.15) !important;
        cursor: pointer;
      }
    }
  }
}

.v-suggestions-input {
  appearance: none;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  border: 1px solid transparent;
  border-radius: 3px;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.08);
  display: -webkit-inline-box;
  display: -ms-inline-flexbox;
  display: inline-flex;
  font-size: 1rem;
  height: 2.25em;
  -webkit-box-pack: start;
  -ms-flex-pack: start;
  justify-content: flex-start;
  line-height: 1.5;
  padding-bottom: calc(0.375em - 1px);
  padding-left: calc(0.625em - 1px);
  padding-right: calc(0.625em - 1px);
  padding-top: calc(0.375em - 1px);
  position: relative;
  vertical-align: top;
  background-color: white;
  border-color: #dbdbdb;
  color: #363636;
  max-width: 100%;
  width: 100%;
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
  &:focus,
  &:active {
    box-shadow: 0 3px 3px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.08);
    outline: none;
  }
}
</style>
